import { useCallback, useEffect } from "react";

import {
  HIGHLIGHT_ANIMATION_DURATION,
  HIGHLIGHT_INITIAL_DISPLAY_TIME
} from "../constants/highlight.constants";

export interface UseDashboardHighlightParams {
  highlightedDashboardId: string | null | undefined;
  setHighlightedDashboard?: (id: string | null) => void;
}

export interface UseDashboardHighlightReturn {
  /**
   * Returns the CSS class name for a dashboard row based on highlight state
   */
  getRowClassName: (dashboardId: string) => string;
}

/**
 * Hook to manage dashboard highlight functionality with proper React patterns.
 * Handles CSS class assignment and automatic cleanup of highlight state.
 */
export function useDashboardHighlight({
  highlightedDashboardId,
  setHighlightedDashboard
}: UseDashboardHighlightParams): UseDashboardHighlightReturn {
  // Handle highlight animation and cleanup
  useEffect(() => {
    if (!highlightedDashboardId) return;

    // Start fade animation after initial display time
    const fadeTimeout = setTimeout(() => {
      const element = document.querySelector(
        `tr[data-row-key="${highlightedDashboardId}"]`
      );
      if (element) {
        // Add fade class to start CSS transition
        element.classList.add("dashboard-clone-fade");
      }
    }, HIGHLIGHT_INITIAL_DISPLAY_TIME);

    // Clean up highlight state and CSS classes after complete animation
    const cleanupTimeout = setTimeout(() => {
      const element = document.querySelector(
        `tr[data-row-key="${highlightedDashboardId}"]`
      );
      if (element) {
        element.classList.remove("dashboard-clone-highlight", "dashboard-clone-fade");
      }

      // Clear highlight state if setter is provided
      if (setHighlightedDashboard) {
        setHighlightedDashboard(null);
      }
    }, HIGHLIGHT_ANIMATION_DURATION);

    // Cleanup function to prevent memory leaks
    return () => {
      clearTimeout(fadeTimeout);
      clearTimeout(cleanupTimeout);
    };
  }, [highlightedDashboardId, setHighlightedDashboard]);

  // Pure function to determine row class name - no side effects
  const getRowClassName = useCallback(
    (dashboardId: string): string => {
      const baseClasses = "dashboard-table__row";

      if (highlightedDashboardId === dashboardId) {
        return `${baseClasses} dashboard-clone-highlight`;
      }

      return baseClasses;
    },
    [highlightedDashboardId]
  );

  return {
    getRowClassName
  };
}
